/**
 * Client-Side Encryption Service
 *
 * Provides end-to-end encryption capabilities using Web Crypto API.
 * All encryption/decryption happens in the browser - the server never
 * has access to encryption keys or decrypted data.
 *
 * Security Features:
 * - AES-256-GCM encryption (authenticated encryption)
 * - PBKDF2 key derivation with high iteration count
 * - Per-user salt (stored server-side, not secret)
 * - Keys stored in-memory only (never persisted)
 * - Random IV for each encryption operation
 *
 * @fileoverview Client-side encryption service for ExpertMD E2EE
 */

/**
 * Encryption Configuration Constants
 */
const ENCRYPTION_CONFIG = {
  // Algorithm for symmetric encryption
  ALGORITHM: 'AES-GCM',
  KEY_LENGTH: 256, // bits

  // Key derivation settings
  KDF_ALGORITHM: 'PBKDF2',
  KDF_HASH: 'SHA-256',
  KDF_ITERATIONS: 100000, // OWASP recommendation for PBKDF2-SHA256

  // GCM settings
  IV_LENGTH: 12, // bytes (96 bits - recommended for GCM)
  TAG_LENGTH: 128, // bits (authentication tag)

  // RSA settings for public key encryption
  RSA_ALGORITHM: 'RSA-OAEP',
  RSA_KEY_SIZE: 4096, // bits (high security)
  RSA_HASH: 'SHA-256',
  RSA_PUBLIC_EXPONENT: new Uint8Array([0x01, 0x00, 0x01]), // 65537
} as const;

/**
 * Custom Error Classes
 */

export class EncryptionError extends Error {
  constructor(
    message: string,
    public readonly code: string,
  ) {
    super(message);
    this.name = 'EncryptionError';
  }
}

export class DecryptionError extends Error {
  constructor(
    message: string,
    public readonly code: string,
  ) {
    super(message);
    this.name = 'DecryptionError';
  }
}

export class KeyDerivationError extends Error {
  constructor(
    message: string,
    public readonly code: string,
  ) {
    super(message);
    this.name = 'KeyDerivationError';
  }
}

/**
 * Type Definitions
 */

export interface EncryptionMetadata {
  algorithm: string;
  keyLength: number;
  ivLength: number;
  timestamp: string;
}

/**
 * EncryptionService Class
 *
 * Singleton service for client-side encryption operations.
 * Manages encryption keys in-memory and provides encrypt/decrypt functions.
 */
class EncryptionService {
  /**
   * Current encryption key (stored in-memory only)
   * @private
   */
  private currentKey: CryptoKey | null = null;

  /**
   * Metadata about current key (for debugging)
   * @private
   */
  private keyMetadata: EncryptionMetadata | null = null;

  /**
   * Check if Web Crypto API is available
   * @private
   */
  private checkCryptoSupport(): void {
    if (!window.crypto || !window.crypto.subtle) {
      throw new EncryptionError(
        'Web Crypto API not available in this browser',
        'CRYPTO_NOT_SUPPORTED',
      );
    }
  }

  /**
   * Derive Encryption Key from Passphrase
   *
   * Uses PBKDF2 to derive a CryptoKey from a user's passphrase and salt.
   * The salt should be unique per user and stored on the server.
   *
   * Security:
   * - 100,000 iterations (OWASP recommendation)
   * - SHA-256 hash function
   * - 256-bit AES key output
   * - Non-extractable key (cannot be exported)
   *
   * @param passphrase - User's passphrase (UTF-8 string)
   * @param salt - Per-user salt (Uint8Array, minimum 16 bytes)
   * @param iterations - Number of PBKDF2 iterations (default: 100000)
   * @returns CryptoKey suitable for AES-GCM encryption
   * @throws KeyDerivationError if derivation fails
   *
   * @example
   * const salt = new Uint8Array([...]); // From server
   * const key = await encryptionService.deriveKey('my-passphrase', salt);
   */
  async deriveKey(
    passphrase: string,
    salt: Uint8Array<ArrayBuffer>,
    iterations: number = ENCRYPTION_CONFIG.KDF_ITERATIONS,
  ): Promise<CryptoKey> {
    this.checkCryptoSupport();

    try {
      // Validate inputs
      if (!passphrase || passphrase.length === 0) {
        throw new KeyDerivationError('Passphrase cannot be empty', 'EMPTY_PASSPHRASE');
      }

      if (!salt || salt.length < 16) {
        throw new KeyDerivationError('Salt must be at least 16 bytes', 'INVALID_SALT');
      }

      // Convert passphrase to key material
      const encoder = new TextEncoder();
      const passphraseKey = await window.crypto.subtle.importKey(
        'raw',
        encoder.encode(passphrase),
        { name: ENCRYPTION_CONFIG.KDF_ALGORITHM },
        false, // Not extractable
        ['deriveBits', 'deriveKey'],
      );

      // Derive AES key from passphrase
      const derivedKey = await window.crypto.subtle.deriveKey(
        {
          name: ENCRYPTION_CONFIG.KDF_ALGORITHM,
          salt: salt,
          iterations: iterations,
          hash: ENCRYPTION_CONFIG.KDF_HASH,
        },
        passphraseKey,
        {
          name: ENCRYPTION_CONFIG.ALGORITHM,
          length: ENCRYPTION_CONFIG.KEY_LENGTH,
        },
        false, // Not extractable (security: cannot export key)
        ['encrypt', 'decrypt'],
      );

      return derivedKey;
    } catch (error) {
      if (error instanceof KeyDerivationError) {
        throw error;
      }

      throw new KeyDerivationError(
        `Failed to derive key: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'DERIVATION_FAILED',
      );
    }
  }

  /**
   * Set Current Encryption Key
   *
   * Stores the encryption key in memory for use in encrypt/decrypt operations.
   * The key is stored only in RAM and never persisted to disk.
   *
   * @param key - CryptoKey to use for encryption/decryption
   *
   * @example
   * const key = await encryptionService.deriveKey(passphrase, salt);
   * encryptionService.setKey(key);
   */
  setKey(key: CryptoKey): void {
    this.currentKey = key;
    this.keyMetadata = {
      algorithm: ENCRYPTION_CONFIG.ALGORITHM,
      keyLength: ENCRYPTION_CONFIG.KEY_LENGTH,
      ivLength: ENCRYPTION_CONFIG.IV_LENGTH,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Get Current Encryption Key
   *
   * Returns the currently stored encryption key, or null if none is set.
   *
   * @returns Current CryptoKey or null
   */
  getKey(): CryptoKey | null {
    return this.currentKey;
  }

  /**
   * Clear Current Encryption Key
   *
   * Removes the encryption key from memory. Should be called on logout
   * or when the user wants to lock their session.
   *
   * @example
   * encryptionService.clearKey();
   */
  clearKey(): void {
    this.currentKey = null;
    this.keyMetadata = null;
  }

  /**
   * Check if Encryption Key is Set
   *
   * @returns True if a key is currently available
   *
   * @example
   * if (encryptionService.hasKey()) {
   *   // Can encrypt/decrypt
   * } else {
   *   // Need to set key first
   * }
   */
  hasKey(): boolean {
    return this.currentKey !== null;
  }

  /**
   * Encrypt Data Payload
   *
   * Encrypts a JavaScript object using AES-256-GCM.
   * The object is serialized to JSON, encrypted, and returned as base64.
   *
   * Format: base64(iv + ciphertext + authTag)
   * - IV: 12 bytes (96 bits)
   * - Ciphertext: Variable length
   * - Auth Tag: 16 bytes (128 bits) - included in GCM output
   *
   * @param data - JavaScript object to encrypt
   * @returns Base64-encoded encrypted payload
   * @throws EncryptionError if encryption fails or key not set
   *
   * @example
   * const sensitiveData = { name: 'John Doe', ssn: '123-45-6789' };
   * const encrypted = await encryptionService.encryptPayload(sensitiveData);
   * // Returns: "base64_encrypted_string"
   */
  async encryptPayload(data: object): Promise<string> {
    this.checkCryptoSupport();

    if (!this.currentKey) {
      throw new EncryptionError('Encryption key not set', 'NO_KEY');
    }

    try {
      // 1. Serialize data to JSON
      const jsonString = JSON.stringify(data);
      const encoder = new TextEncoder();
      const plaintext = encoder.encode(jsonString);

      // 2. Generate random IV (initialization vector)
      const iv = window.crypto.getRandomValues(new Uint8Array(ENCRYPTION_CONFIG.IV_LENGTH));

      // 3. Encrypt data using AES-GCM
      const ciphertext = await window.crypto.subtle.encrypt(
        {
          name: ENCRYPTION_CONFIG.ALGORITHM,
          iv: iv,
          tagLength: ENCRYPTION_CONFIG.TAG_LENGTH,
        },
        this.currentKey,
        plaintext,
      );

      // 4. Combine IV + ciphertext (ciphertext includes auth tag)
      const combined = new Uint8Array(iv.length + ciphertext.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(ciphertext), iv.length);

      // 5. Convert to base64 for transport
      return this.arrayBufferToBase64(combined);
    } catch (error) {
      throw new EncryptionError(
        `Failed to encrypt payload: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'ENCRYPTION_FAILED',
      );
    }
  }

  /**
   * Decrypt Data Payload
   *
   * Decrypts a base64-encoded encrypted payload back to a JavaScript object.
   *
   * @param ciphertext - Base64-encoded encrypted payload (from encryptPayload)
   * @returns Decrypted JavaScript object
   * @throws DecryptionError if decryption fails, key not set, or data corrupted
   *
   * @example
   * const encrypted = "base64_encrypted_string";
   * const decrypted = await encryptionService.decryptPayload(encrypted);
   * // Returns: { name: 'John Doe', ssn: '123-45-6789' }
   */
  async decryptPayload(ciphertext: string): Promise<object> {
    this.checkCryptoSupport();

    if (!this.currentKey) {
      throw new DecryptionError('Decryption key not set', 'NO_KEY');
    }

    try {
      // 1. Decode base64
      const combined = this.base64ToArrayBuffer(ciphertext);

      // 2. Extract IV and ciphertext
      const iv = combined.slice(0, ENCRYPTION_CONFIG.IV_LENGTH);
      const encryptedData = combined.slice(ENCRYPTION_CONFIG.IV_LENGTH);

      // 3. Decrypt using AES-GCM
      const decrypted = await window.crypto.subtle.decrypt(
        {
          name: ENCRYPTION_CONFIG.ALGORITHM,
          iv: iv,
          tagLength: ENCRYPTION_CONFIG.TAG_LENGTH,
        },
        this.currentKey,
        encryptedData,
      );

      // 4. Convert back to string and parse JSON
      const decoder = new TextDecoder();
      const jsonString = decoder.decode(decrypted);
      const data = JSON.parse(jsonString);

      return data;
    } catch (error) {
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.name === 'OperationError') {
          throw new DecryptionError(
            'Decryption failed - wrong passphrase or corrupted data',
            'DECRYPTION_FAILED',
          );
        }

        if (error.name === 'SyntaxError') {
          throw new DecryptionError('Decrypted data is not valid JSON', 'INVALID_JSON');
        }
      }

      throw new DecryptionError(
        `Failed to decrypt payload: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'DECRYPTION_FAILED',
      );
    }
  }

  /**
   * Encrypt File
   *
   * Encrypts a file using AES-256-GCM.
   * Returns an encrypted Blob with the same format as encryptPayload.
   *
   * @param file - File object to encrypt
   * @returns Encrypted Blob (can be uploaded to server)
   * @throws EncryptionError if encryption fails
   *
   * @example
   * const file = document.querySelector('input[type="file"]').files[0];
   * const encryptedBlob = await encryptionService.encryptFile(file);
   * // Upload encryptedBlob to server
   */
  async encryptFile(file: File): Promise<Blob> {
    this.checkCryptoSupport();

    if (!this.currentKey) {
      throw new EncryptionError('Encryption key not set', 'NO_KEY');
    }

    try {
      // 1. Read file as ArrayBuffer
      const fileData = await file.arrayBuffer();

      // 2. Generate random IV
      const iv = window.crypto.getRandomValues(new Uint8Array(ENCRYPTION_CONFIG.IV_LENGTH));

      // 3. Encrypt file data
      const ciphertext = await window.crypto.subtle.encrypt(
        {
          name: ENCRYPTION_CONFIG.ALGORITHM,
          iv: iv,
          tagLength: ENCRYPTION_CONFIG.TAG_LENGTH,
        },
        this.currentKey,
        fileData,
      );

      // 4. Combine IV + ciphertext
      const combined = new Uint8Array(iv.length + ciphertext.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(ciphertext), iv.length);

      // 5. Return as Blob
      return new Blob([combined], { type: 'application/octet-stream' });
    } catch (error) {
      throw new EncryptionError(
        `Failed to encrypt file: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'FILE_ENCRYPTION_FAILED',
      );
    }
  }

  /**
   * Decrypt File
   *
   * Decrypts an encrypted Blob back to original file data.
   *
   * @param encryptedBlob - Encrypted Blob (from encryptFile or server)
   * @returns Decrypted Blob with original file data
   * @throws DecryptionError if decryption fails
   *
   * @example
   * const response = await fetch(fileUrl);
   * const encryptedBlob = await response.blob();
   * const decryptedBlob = await encryptionService.decryptFile(encryptedBlob);
   * // Use decryptedBlob (e.g., create download link)
   */
  async decryptFile(encryptedBlob: Blob): Promise<Blob> {
    this.checkCryptoSupport();

    if (!this.currentKey) {
      throw new DecryptionError('Decryption key not set', 'NO_KEY');
    }

    try {
      // 1. Read blob as ArrayBuffer
      const combined = await encryptedBlob.arrayBuffer();
      const combinedArray = new Uint8Array(combined);

      // 2. Extract IV and ciphertext
      const iv = combinedArray.slice(0, ENCRYPTION_CONFIG.IV_LENGTH);
      const encryptedData = combinedArray.slice(ENCRYPTION_CONFIG.IV_LENGTH);

      // 3. Decrypt
      const decrypted = await window.crypto.subtle.decrypt(
        {
          name: ENCRYPTION_CONFIG.ALGORITHM,
          iv: iv,
          tagLength: ENCRYPTION_CONFIG.TAG_LENGTH,
        },
        this.currentKey,
        encryptedData,
      );

      // 4. Return as Blob (original MIME type should be known from metadata)
      return new Blob([decrypted]);
    } catch (error) {
      throw new DecryptionError(
        `Failed to decrypt file: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'FILE_DECRYPTION_FAILED',
      );
    }
  }

  /**
   * Utility: Convert ArrayBuffer to Base64
   * @private
   */
  private arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
    let binary = '';
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const len = bytes.byteLength;

    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    return btoa(binary);
  }

  /**
   * Utility: Convert Base64 to ArrayBuffer
   * @private
   */
  private base64ToArrayBuffer(base64: string): Uint8Array<ArrayBuffer> {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
  }

  /**
   * Get Encryption Metadata (for debugging)
   *
   * Returns information about the current encryption key.
   * Does NOT return the actual key (it's non-extractable).
   *
   * @returns Metadata about current key or null
   */
  getMetadata(): EncryptionMetadata | null {
    return this.keyMetadata;
  }

  // ============================================
  // PUBLIC KEY ENCRYPTION (RSA-OAEP)
  // ============================================

  /**
   * Generate RSA Key Pair
   *
   * Generates a new RSA-OAEP key pair for asymmetric encryption.
   * Used during encryption setup to create public/private keys.
   *
   * @returns Object with public and private CryptoKey objects
   * @throws EncryptionError if key generation fails
   *
   * @example
   * const { publicKey, privateKey } = await encryptionService.generateKeyPair();
   * const publicPem = await encryptionService.exportPublicKey(publicKey);
   * const privatePem = await encryptionService.exportPrivateKey(privateKey);
   */
  async generateKeyPair(): Promise<{ publicKey: CryptoKey; privateKey: CryptoKey }> {
    this.checkCryptoSupport();

    try {
      const keyPair = await window.crypto.subtle.generateKey(
        {
          name: ENCRYPTION_CONFIG.RSA_ALGORITHM,
          modulusLength: ENCRYPTION_CONFIG.RSA_KEY_SIZE,
          publicExponent: ENCRYPTION_CONFIG.RSA_PUBLIC_EXPONENT,
          hash: ENCRYPTION_CONFIG.RSA_HASH,
        },
        true, // Extractable (we need to export for storage)
        ['encrypt', 'decrypt'],
      );

      return {
        publicKey: keyPair.publicKey,
        privateKey: keyPair.privateKey,
      };
    } catch (error) {
      throw new EncryptionError(
        `Failed to generate key pair: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'KEY_GENERATION_FAILED',
      );
    }
  }

  /**
   * Export Public Key to PEM Format
   *
   * Exports a CryptoKey public key to PEM format for storage.
   *
   * @param publicKey - CryptoKey public key
   * @returns PEM-formatted public key string
   * @throws EncryptionError if export fails
   */
  async exportPublicKey(publicKey: CryptoKey): Promise<string> {
    this.checkCryptoSupport();

    try {
      const exported = await window.crypto.subtle.exportKey('spki', publicKey);
      const exportedAsString = this.arrayBufferToBase64(new Uint8Array(exported));
      return `-----BEGIN PUBLIC KEY-----\n${exportedAsString}\n-----END PUBLIC KEY-----`;
    } catch (error) {
      throw new EncryptionError(
        `Failed to export public key: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'PUBLIC_KEY_EXPORT_FAILED',
      );
    }
  }

  /**
   * Export Private Key to PEM Format
   *
   * Exports a CryptoKey private key to PEM format.
   * IMPORTANT: This should be encrypted with the user's passphrase before storage!
   *
   * @param privateKey - CryptoKey private key
   * @returns PEM-formatted private key string
   * @throws EncryptionError if export fails
   */
  async exportPrivateKey(privateKey: CryptoKey): Promise<string> {
    this.checkCryptoSupport();

    try {
      const exported = await window.crypto.subtle.exportKey('pkcs8', privateKey);
      const exportedAsString = this.arrayBufferToBase64(new Uint8Array(exported));
      return `-----BEGIN PRIVATE KEY-----\n${exportedAsString}\n-----END PRIVATE KEY-----`;
    } catch (error) {
      throw new EncryptionError(
        `Failed to export private key: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'PRIVATE_KEY_EXPORT_FAILED',
      );
    }
  }

  /**
   * Import Public Key from PEM Format
   *
   * Imports a PEM-formatted public key for use in encryption.
   *
   * @param pem - PEM-formatted public key string
   * @returns CryptoKey public key
   * @throws EncryptionError if import fails
   */
  async importPublicKey(pem: string): Promise<CryptoKey> {
    this.checkCryptoSupport();

    try {
      // Remove PEM header/footer and decode base64
      const pemContents = pem
        .replace('-----BEGIN PUBLIC KEY-----', '')
        .replace('-----END PUBLIC KEY-----', '')
        .replace(/\s/g, '');
      const binaryDer = this.base64ToArrayBuffer(pemContents);

      const publicKey = await window.crypto.subtle.importKey(
        'spki',
        binaryDer,
        {
          name: ENCRYPTION_CONFIG.RSA_ALGORITHM,
          hash: ENCRYPTION_CONFIG.RSA_HASH,
        },
        true,
        ['encrypt'],
      );

      return publicKey;
    } catch (error) {
      throw new EncryptionError(
        `Failed to import public key: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'PUBLIC_KEY_IMPORT_FAILED',
      );
    }
  }

  /**
   * Import Private Key from PEM Format
   *
   * Imports a PEM-formatted private key for use in decryption.
   *
   * @param pem - PEM-formatted private key string
   * @returns CryptoKey private key
   * @throws EncryptionError if import fails
   */
  async importPrivateKey(pem: string): Promise<CryptoKey> {
    this.checkCryptoSupport();

    try {
      // Remove PEM header/footer and decode base64
      const pemContents = pem
        .replace('-----BEGIN PRIVATE KEY-----', '')
        .replace('-----END PRIVATE KEY-----', '')
        .replace(/\s/g, '');
      const binaryDer = this.base64ToArrayBuffer(pemContents);

      const privateKey = await window.crypto.subtle.importKey(
        'pkcs8',
        binaryDer,
        {
          name: ENCRYPTION_CONFIG.RSA_ALGORITHM,
          hash: ENCRYPTION_CONFIG.RSA_HASH,
        },
        true,
        ['decrypt'],
      );

      return privateKey;
    } catch (error) {
      throw new EncryptionError(
        `Failed to import private key: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'PRIVATE_KEY_IMPORT_FAILED',
      );
    }
  }

  /**
   * Encrypt with Public Key (RSA-OAEP)
   *
   * Encrypts data using RSA-OAEP public key encryption.
   * Used by intake form to encrypt sensitive data without authentication.
   *
   * Note: RSA can only encrypt limited data size. For larger payloads,
   * this method actually uses hybrid encryption:
   * 1. Generate random AES key
   * 2. Encrypt data with AES key
   * 3. Encrypt AES key with RSA public key
   * 4. Return combined result
   *
   * @param data - JavaScript object to encrypt
   * @param publicKeyPem - PEM-formatted public key
   * @returns Base64-encoded encrypted payload
   * @throws EncryptionError if encryption fails
   */
  async encryptWithPublicKey(data: object, publicKeyPem: string): Promise<string> {
    this.checkCryptoSupport();

    try {
      // 1. Import public key
      const publicKey = await this.importPublicKey(publicKeyPem);

      // 2. Generate random AES key for data encryption
      const aesKey = await window.crypto.subtle.generateKey(
        {
          name: ENCRYPTION_CONFIG.ALGORITHM,
          length: ENCRYPTION_CONFIG.KEY_LENGTH,
        },
        true, // Extractable
        ['encrypt'],
      );

      // 3. Encrypt data with AES key
      const jsonString = JSON.stringify(data);
      const encoder = new TextEncoder();
      const plaintext = encoder.encode(jsonString);

      const iv = window.crypto.getRandomValues(new Uint8Array(ENCRYPTION_CONFIG.IV_LENGTH));

      const ciphertext = await window.crypto.subtle.encrypt(
        {
          name: ENCRYPTION_CONFIG.ALGORITHM,
          iv: iv,
          tagLength: ENCRYPTION_CONFIG.TAG_LENGTH,
        },
        aesKey,
        plaintext,
      );

      // 4. Export AES key and encrypt with RSA public key
      const exportedAesKey = await window.crypto.subtle.exportKey('raw', aesKey);
      const encryptedAesKey = await window.crypto.subtle.encrypt(
        {
          name: ENCRYPTION_CONFIG.RSA_ALGORITHM,
        },
        publicKey,
        exportedAesKey,
      );

      // 5. Combine: encryptedAesKey length (4 bytes) + encryptedAesKey + iv + ciphertext
      const encryptedAesKeyArray = new Uint8Array(encryptedAesKey);
      const aesKeyLength = new Uint8Array(4);
      new DataView(aesKeyLength.buffer).setUint32(0, encryptedAesKeyArray.length, false);

      const combined = new Uint8Array(
        4 + encryptedAesKeyArray.length + iv.length + ciphertext.byteLength,
      );
      combined.set(aesKeyLength, 0);
      combined.set(encryptedAesKeyArray, 4);
      combined.set(iv, 4 + encryptedAesKeyArray.length);
      combined.set(new Uint8Array(ciphertext), 4 + encryptedAesKeyArray.length + iv.length);

      // 6. Return as base64
      return this.arrayBufferToBase64(combined);
    } catch (error) {
      throw new EncryptionError(
        `Failed to encrypt with public key: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'PUBLIC_KEY_ENCRYPTION_FAILED',
      );
    }
  }

  /**
   * Decrypt with Private Key (RSA-OAEP)
   *
   * Decrypts data that was encrypted with encryptWithPublicKey.
   * Used by doctors to decrypt intake form submissions.
   *
   * @param ciphertext - Base64-encoded encrypted payload
   * @param privateKeyPem - PEM-formatted private key
   * @returns Decrypted JavaScript object
   * @throws DecryptionError if decryption fails
   */
  async decryptWithPrivateKey(ciphertext: string, privateKeyPem: string): Promise<object> {
    this.checkCryptoSupport();

    try {
      // 1. Import private key
      const privateKey = await this.importPrivateKey(privateKeyPem);

      // 2. Decode base64
      const combined = this.base64ToArrayBuffer(ciphertext);

      // 3. Extract components
      const aesKeyLength = new DataView(combined.buffer).getUint32(0, false);
      const encryptedAesKey = combined.slice(4, 4 + aesKeyLength);
      const iv = combined.slice(4 + aesKeyLength, 4 + aesKeyLength + ENCRYPTION_CONFIG.IV_LENGTH);
      const encryptedData = combined.slice(4 + aesKeyLength + ENCRYPTION_CONFIG.IV_LENGTH);

      // 4. Decrypt AES key with RSA private key
      const decryptedAesKeyBuffer = await window.crypto.subtle.decrypt(
        {
          name: ENCRYPTION_CONFIG.RSA_ALGORITHM,
        },
        privateKey,
        encryptedAesKey,
      );

      // 5. Import decrypted AES key
      const aesKey = await window.crypto.subtle.importKey(
        'raw',
        decryptedAesKeyBuffer,
        {
          name: ENCRYPTION_CONFIG.ALGORITHM,
          length: ENCRYPTION_CONFIG.KEY_LENGTH,
        },
        false,
        ['decrypt'],
      );

      // 6. Decrypt data with AES key
      const decrypted = await window.crypto.subtle.decrypt(
        {
          name: ENCRYPTION_CONFIG.ALGORITHM,
          iv: iv,
          tagLength: ENCRYPTION_CONFIG.TAG_LENGTH,
        },
        aesKey,
        encryptedData,
      );

      // 7. Parse JSON
      const decoder = new TextDecoder();
      const jsonString = decoder.decode(decrypted);
      const data = JSON.parse(jsonString);

      return data;
    } catch (error) {
      throw new DecryptionError(
        `Failed to decrypt with private key: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'PRIVATE_KEY_DECRYPTION_FAILED',
      );
    }
  }

  /**
   * Encrypt Private Key with Passphrase
   *
   * Encrypts a PEM-formatted private key using the user's passphrase-derived key.
   * This is used to securely store the private key in the database.
   *
   * @param privateKeyPem - PEM-formatted private key
   * @param passphrase - User's passphrase
   * @param salt - User's salt
   * @returns Base64-encoded encrypted private key
   * @throws EncryptionError if encryption fails
   */
  async encryptPrivateKeyWithPassphrase(
    privateKeyPem: string,
    passphrase: string,
    salt: Uint8Array<ArrayBuffer>,
  ): Promise<string> {
    this.checkCryptoSupport();

    try {
      // Derive key from passphrase
      const key = await this.deriveKey(passphrase, salt);

      // Encrypt private key PEM as text
      const encoder = new TextEncoder();
      const privateKeyBytes = encoder.encode(privateKeyPem);

      const iv = window.crypto.getRandomValues(new Uint8Array(ENCRYPTION_CONFIG.IV_LENGTH));

      const ciphertext = await window.crypto.subtle.encrypt(
        {
          name: ENCRYPTION_CONFIG.ALGORITHM,
          iv: iv,
          tagLength: ENCRYPTION_CONFIG.TAG_LENGTH,
        },
        key,
        privateKeyBytes,
      );

      // Combine IV + ciphertext
      const combined = new Uint8Array(iv.length + ciphertext.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(ciphertext), iv.length);

      return this.arrayBufferToBase64(combined);
    } catch (error) {
      throw new EncryptionError(
        `Failed to encrypt private key: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'PRIVATE_KEY_ENCRYPTION_FAILED',
      );
    }
  }

  /**
   * Decrypt Private Key with Passphrase
   *
   * Decrypts an encrypted private key using the user's passphrase.
   * This is used when the user unlocks their session.
   *
   * @param encryptedPrivateKey - Base64-encoded encrypted private key
   * @param passphrase - User's passphrase
   * @param salt - User's salt
   * @returns PEM-formatted private key
   * @throws DecryptionError if decryption fails
   */
  async decryptPrivateKeyWithPassphrase(
    encryptedPrivateKey: string,
    passphrase: string,
    salt: Uint8Array<ArrayBuffer>,
  ): Promise<string> {
    this.checkCryptoSupport();

    try {
      // Derive key from passphrase
      const key = await this.deriveKey(passphrase, salt);

      // Decode and extract IV and ciphertext
      const combined = this.base64ToArrayBuffer(encryptedPrivateKey);
      const iv = combined.slice(0, ENCRYPTION_CONFIG.IV_LENGTH);
      const encryptedData = combined.slice(ENCRYPTION_CONFIG.IV_LENGTH);

      // Decrypt
      const decrypted = await window.crypto.subtle.decrypt(
        {
          name: ENCRYPTION_CONFIG.ALGORITHM,
          iv: iv,
          tagLength: ENCRYPTION_CONFIG.TAG_LENGTH,
        },
        key,
        encryptedData,
      );

      // Convert back to string
      const decoder = new TextDecoder();
      const privateKeyPem = decoder.decode(decrypted);

      return privateKeyPem;
    } catch (error) {
      throw new DecryptionError(
        `Failed to decrypt private key: ${error instanceof Error ? error.message : 'Wrong passphrase'}`,
        'PRIVATE_KEY_DECRYPTION_FAILED',
      );
    }
  }

  /**
   * Field-Level Encryption
   * ======================
   * Simplified encryption for individual database fields.
   * Each field is encrypted with RSA public key and stored with "ENC:" prefix.
   */

  /**
   * Encrypt a single string field with RSA public key
   *
   * @param value - Plaintext string to encrypt
   * @param publicKeyPem - PEM-formatted RSA public key
   * @returns Encrypted string with "ENC:" prefix, or original value if empty/undefined
   * @throws EncryptionError if encryption fails
   */
  async encryptField(value: string | null | undefined, publicKeyPem: string): Promise<string> {
    // Return empty string for null/undefined values
    if (!value || value.trim() === '') {
      return value || '';
    }

    // Validate public key before attempting encryption
    if (
      !publicKeyPem ||
      typeof publicKeyPem !== 'string' ||
      !publicKeyPem.includes('-----BEGIN PUBLIC KEY-----')
    ) {
      throw new EncryptionError(
        "Invalid or missing public key. The doctor's encryption may not be fully configured.",
        'INVALID_PUBLIC_KEY',
      );
    }

    this.checkCryptoSupport();

    try {
      // Encode string to bytes
      const encoder = new TextEncoder();
      const data = encoder.encode(value);

      // Use hybrid encryption (AES+RSA) for fields > 400 bytes, direct RSA for small fields
      if (data.byteLength > 400) {
        // Hybrid: generate AES key, encrypt data with AES, encrypt AES key with RSA
        const result = await this.encryptWithPublicKey({ __field: value }, publicKeyPem);
        return `HENC:${result}`;
      }

      // Direct RSA for small fields
      const publicKey = await this.importPublicKey(publicKeyPem);
      const encrypted = await window.crypto.subtle.encrypt(
        {
          name: ENCRYPTION_CONFIG.RSA_ALGORITHM,
        },
        publicKey,
        data,
      );

      // Encode as base64 and prefix
      const base64 = this.arrayBufferToBase64(encrypted);
      return `ENC:${base64}`;
    } catch (error) {
      if (error instanceof EncryptionError) {
        throw error;
      }
      console.error('Field encryption failed:', error);
      throw new EncryptionError(
        `Failed to encrypt field: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'FIELD_ENCRYPTION_FAILED',
      );
    }
  }

  /**
   * Validate Encrypted Field
   *
   * Attempts to decrypt an encrypted field to verify encryption integrity.
   * This is a lightweight validation that ensures the encryption format is valid.
   *
   * @param encryptedValue - Encrypted string with "ENC:" prefix
   * @param publicKeyPem - Public key PEM (used to derive private key for validation)
   * @throws DecryptionError if validation fails
   * @private
   */
  private async validateEncryptedField(
    encryptedValue: string,
    publicKeyPem: string,
  ): Promise<void> {
    // Basic format validation
    if (!encryptedValue.startsWith('ENC:')) {
      throw new DecryptionError(
        'Invalid encryption format - missing ENC: prefix',
        'INVALID_FORMAT',
      );
    }

    const base64 = encryptedValue.slice(4);
    if (!base64 || base64.length === 0) {
      throw new DecryptionError('Invalid encryption format - empty base64 data', 'INVALID_FORMAT');
    }

    // Validate base64 encoding
    try {
      this.base64ToArrayBuffer(base64);
    } catch (error) {
      throw new DecryptionError(
        'Invalid encryption format - corrupted base64 encoding',
        'INVALID_BASE64',
      );
    }

    // Note: We cannot validate actual decryption here without the private key,
    // but format validation catches most issues
  }

  /**
   * Decrypt a single string field with RSA private key
   *
   * @param encryptedValue - Encrypted string with "ENC:" prefix, or plaintext
   * @param privateKeyPem - PEM-formatted RSA private key
   * @returns Decrypted plaintext string, or original value if not encrypted
   * @throws DecryptionError if decryption fails
   */
  async decryptField(
    encryptedValue: string | null | undefined,
    privateKeyPem: string,
  ): Promise<string> {
    // Return as-is if null/undefined or not encrypted
    if (
      !encryptedValue ||
      (!encryptedValue.startsWith('ENC:') && !encryptedValue.startsWith('HENC:'))
    ) {
      return encryptedValue || '';
    }

    this.checkCryptoSupport();

    // Handle hybrid-encrypted fields (large values encrypted with AES+RSA)
    if (encryptedValue.startsWith('HENC:')) {
      try {
        const hybridPayload = encryptedValue.slice(5); // Remove "HENC:"
        const decryptedObj = (await this.decryptWithPrivateKey(hybridPayload, privateKeyPem)) as {
          __field: string;
        };
        return decryptedObj.__field || '';
      } catch (error) {
        throw new DecryptionError(
          `Failed to decrypt hybrid-encrypted field: ${error instanceof Error ? error.message : 'Unknown error'}`,
          'HYBRID_DECRYPTION_FAILED',
        );
      }
    }

    try {
      // Validate format first
      const base64 = encryptedValue.slice(4); // Remove "ENC:"
      if (!base64 || base64.length === 0) {
        throw new DecryptionError(
          'Invalid encryption format - empty encrypted data',
          'INVALID_FORMAT',
        );
      }

      // Import RSA private key
      const privateKey = await this.importPrivateKey(privateKeyPem);

      // Decode base64
      let encrypted: Uint8Array<ArrayBuffer>;
      try {
        encrypted = this.base64ToArrayBuffer(base64);
      } catch (base64Error) {
        console.error('Base64 decoding failed:', base64Error);
        throw new DecryptionError(
          `Invalid base64 encoding in encrypted field. Data length: ${base64.length}, Preview: "${base64.substring(0, 50)}..."`,
          'INVALID_BASE64',
        );
      }

      // Decrypt with RSA private key
      let decrypted: ArrayBuffer;
      try {
        decrypted = await window.crypto.subtle.decrypt(
          {
            name: ENCRYPTION_CONFIG.RSA_ALGORITHM,
          },
          privateKey,
          encrypted,
        );
      } catch (decryptError) {
        console.error('RSA decryption failed:', decryptError);
        throw new DecryptionError(
          `RSA decryption failed - data may be encrypted with a different key. Encrypted data size: ${encrypted.byteLength} bytes`,
          'RSA_DECRYPTION_FAILED',
        );
      }

      // Decode bytes to string
      const decoder = new TextDecoder();
      return decoder.decode(decrypted);
    } catch (error) {
      if (error instanceof DecryptionError) {
        throw error;
      }
      console.error('Field decryption failed with unexpected error:', error);
      throw new DecryptionError(
        `Failed to decrypt field: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'FIELD_DECRYPTION_FAILED',
      );
    }
  }
}

/**
 * Singleton Instance
 *
 * Export a single instance of EncryptionService to be used throughout the app.
 */
export const encryptionService = new EncryptionService();

/**
 * Export class for testing
 */
export { EncryptionService };
